import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthContext } from '../auth/AuthContext';
import { login } from '../services/auth';

const AdminLogin = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShow] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const navigate = useNavigate();
  const { setToken: setAuthToken, setUser: setAuthUser, setRole: setAuthRole, refreshAuth } = useContext(AuthContext) ?? {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, role, user } = await login(email, password, rememberMe);
      setAuthToken?.(token ?? null);
      setAuthUser?.(user ?? null);
      setAuthRole?.(role ?? user?.role ?? null);
      refreshAuth?.();
      navigate('/admin');
    } catch (err) {
      const message = err?.message || 'Invalid credentials';
      if (/locked/i.test(message)) {
        toast.error(message, { toastId: 'admin-login-lock' });
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const Eye = ({ off }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      {off ? (
        <>
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            d="M10.584 10.59A3 3 0 0012 15a3 3 0 002.413-4.412M9.88 4.848A9.96 9.96 0 0112 4c4.477 0 8.268 2.943 9.542 7a9.97 9.97 0 01-3.234 4.5M6.228 6.23A9.971 9.971 0 002.458 12c1.274 4.057 5.065 7 9.542 7 1.398 0 2.73-.286 3.936-.804" />
        </>
      ) : (
        <>
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z" />
          <circle cx="12" cy="12" r="3" strokeWidth="2" />
        </>
      )}
    </svg>
  );

  return (
    <div className="login-root" style={styles.container}>
      <ToastContainer
        position="top-center"
        autoClose={6000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable={false}
        theme="colored"
        className="z-toast"
      />
      {/* Scoped CSS so it looks like the tutor login */}
      <style>{css}</style>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="login-card"
        style={styles.card}
      >
        <img src="/logo.png" alt="Logo" style={styles.logo} />

        <h2 style={styles.title}>Admin Login</h2>

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <div style={styles.floatingInputContainer}>
            <input
              className="login-input"
              type="email"
              aria-label="Email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
            />
            <label
              style={{
                ...styles.floatingLabel,
                top: email ? '-0.95rem' : '1.05rem',
              }}
            >
              Email
            </label>
          </div>

          <div style={styles.floatingInputContainer}>
            <input
              className="login-input"
              type={showPassword ? 'text' : 'password'}
              aria-label="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
            />
            <label
              style={{
                ...styles.floatingLabel,
                top: password ? '-0.95rem' : '1.05rem',
              }}
            >
              Password
            </label>

            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="pw-toggle"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
              style={styles.passwordToggleBtn}
            >
              <Eye off={showPassword} />
            </button>
          </div>

          <label style={styles.rememberRow} className="remember-control">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={styles.rememberCheckbox}
              aria-label="Remember me on this device"
            />
            <span>Remember me on this device</span>
          </label>

          <button
            type="submit"
            className="login-btn"
            disabled={loading}
            style={{
              ...styles.button,
              opacity: loading ? 0.85 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {error && (
          <div className="login-toast" style={styles.toast} role="alert">
            {error}
          </div>
        )}
      </motion.div>

      <footer style={styles.footer}>
        (c) {new Date().getFullYear()} Apex Academic Centre
      </footer>
    </div>
  );
};

export default AdminLogin;

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: 'Montserrat, sans-serif',
    background:
      'radial-gradient(1200px 600px at 0% -10%, rgba(14,165,233,.08), transparent 60%),' +
      'radial-gradient(1200px 600px at 100% 110%, rgba(2,132,199,.06), transparent 60%),' +
      '#f1f5f9',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    padding: '2rem',
    borderRadius: 18,
    boxShadow: '0 12px 40px rgba(0, 21, 74, 0.12)',
    textAlign: 'left',
    position: 'relative',
    backgroundColor: '#fff',
    color: '#111827',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  logo: {
    width: 84,
    display: 'block',
    margin: '0 auto 1rem',
  },
  title: {
    fontSize: '1.75rem',
    marginBottom: '1rem',
    color: '#00154A',
    fontWeight: 800,
    letterSpacing: '.2px',
    textAlign: 'center',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '1.2rem' },
  floatingInputContainer: { position: 'relative', width: '100%' },
  floatingLabel: {
    position: 'absolute',
    left: '0.75rem',
    fontSize: '0.875rem',
    color: '#6b7280',
    background: 'transparent',
    transition: '0.2s ease',
    pointerEvents: 'none',
    fontWeight: 600,
  },
  input: {
    width: '100%',
    padding: '0.9rem 2.5rem 0.9rem 0.9rem',
    border: '1px solid #d1d5db',
    borderRadius: 18,
    fontSize: '1rem',
    outline: 'none',
    fontFamily: 'Montserrat, sans-serif',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    boxSizing: 'border-box',
  },
  passwordToggleBtn: {
    position: 'absolute',
    right: '0.6rem',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    width: 36,
    height: 36,
    borderRadius: 18,
    border: 'none',
    background: 'transparent',
    display: 'grid',
    placeItems: 'center',
    color: '#6b7280',
  },
  rememberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    fontSize: '0.95rem',
    color: '#374151',
    userSelect: 'none',
  },
  rememberCheckbox: {
    width: 18,
    height: 18,
    cursor: 'pointer',
    accentColor: '#00154A',
  },
  button: {
    width: '100%',
    padding: '0.9rem',
    fontSize: '1rem',
    color: '#fff',
    border: 'none',
    borderRadius: 18,
    transition: 'background .25s ease, box-shadow .25s ease, transform .08s ease',
    fontWeight: 800,
    letterSpacing: '.2px',
  },
  toast: {
    marginTop: '1rem',
    width: '100%',
    padding: '10px 12px',
    borderRadius: 18,
    background: '#fee2e2',
    color: '#b91c1c',
    fontSize: '0.95rem',
    fontWeight: 600,
    boxSizing: 'border-box',
  },
  footer: {
    marginTop: '2rem',
    color: '#6b7280',
    fontFamily: 'Montserrat, sans-serif',
  },
};

const css = `
  /* Focus ring to match tutor login */
  .login-root .login-card .login-input:focus {
    border-color: #00154A;
    box-shadow: inset 0 0 0 1px #00154A, 0 0 0 3px rgba(0,21,74,.12);
  }

  /* CTA consistent with tutor dash login (green base, midnight hover) */
  .login-root .login-btn {
    background: #02A552;
  }
  .login-root .login-btn:hover {
    background: #00154A;
    box-shadow: 0 8px 18px rgba(0,21,74,.15);
  }
  .login-root .login-btn:active { transform: translateY(1px); }
  .login-root .login-btn:disabled { opacity: .85; cursor: not-allowed; box-shadow: none; }

  /* Eye toggle hover color */
  .login-root .pw-toggle:hover { color: #00154A; }
`;










